import { useState, useEffect } from 'react';
import { getTables } from '../api/tables.api.js';
import { IcoClose } from './common/Icons.jsx';
import styles from './MoveTabSheet.module.css';

const fmt = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

// Dos operaciones distintas en una sola hoja porque el mesero llega con la
// misma duda —"esta cuenta va a otro lado"— y solo al final sabe si es porque
// se cambiaron de mesa o porque se juntaron con otra.
export default function MoveTabSheet({ open, tab, tabs, onMove, onMerge, onClose }) {
  const [modo,     setModo]     = useState('mover');
  const [mesas,    setMesas]    = useState([]);
  const [error,    setError]    = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setModo('mover'); setError(null);
    getTables().then((r) => setMesas(r.data ?? [])).catch(() => setMesas([]));
  }, [open]);

  if (!open || !tab) return null;

  const ejecutar = async (fn) => {
    setEnviando(true); setError(null);
    try { await fn(); onClose(); }
    catch (err) { setError(err.response?.data?.error ?? 'No se pudo completar'); }
    finally { setEnviando(false); }
  };

  // Una mesa ocupada por OTRA cuenta no se ofrece: el servidor la rechazaría
  // con 409 y el mesero se llevaría el viaje en balde.
  const libres = mesas.filter((m) => !m.occupancy || m.occupancy.tab_id === tab.id);
  const otras  = (tabs ?? []).filter((t) => t.id !== tab.id);

  return (
    <div className={styles.scrim} role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.sheet} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <div>
            <h2 className={styles.title}>{tab.name}</h2>
            <p className={styles.sub}>{tab.items?.length ?? 0} partidas</p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Cerrar"><IcoClose size={16} /></button>
        </div>

        <div className={styles.tabs}>
          <button className={modo === 'mover' ? styles.tabOn : styles.tab} onClick={() => { setModo('mover'); setError(null); }}>
            Cambiar de mesa
          </button>
          <button className={modo === 'juntar' ? styles.tabOn : styles.tab} onClick={() => { setModo('juntar'); setError(null); }}>
            Juntar con otra
          </button>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        {modo === 'mover' ? (
          <div className={styles.lista}>
            {libres.length === 0 && <p className={styles.vacio}>No hay mesas libres.</p>}
            {libres.map((m) => (
              <button key={m._id} className={styles.opcion} disabled={enviando}
                      onClick={() => ejecutar(() => onMove(tab, m._id))}>
                <strong>{m.name}</strong>
                <small>{m.zone} · {m.seats} lugares</small>
              </button>
            ))}
            <button className={styles.opcionGhost} disabled={enviando}
                    onClick={() => ejecutar(() => onMove(tab, null))}>
              Dejarla sin mesa <small>(para llevar)</small>
            </button>
          </div>
        ) : (
          <div className={styles.lista}>
            {otras.length === 0 && <p className={styles.vacio}>No hay otra cuenta abierta con la cual juntarla.</p>}
            {otras.map((t) => (
              <button key={t.id} className={styles.opcion} disabled={enviando}
                      onClick={() => ejecutar(() => onMerge(tab, t.id))}>
                <strong>{t.name}</strong>
                <small>{t.items?.length ?? 0} partidas · {fmt((t.items ?? []).reduce((s, i) => s + i.total, 0))}</small>
              </button>
            ))}
            {otras.length > 0 && (
              <p className={styles.aviso}>
                Los platillos de <strong>{tab.name}</strong> y sus comandas se pasan a la
                cuenta que elijas, y esta se cierra.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
